import { BERTH_COUNT } from '@/fleet/Fleet';

export interface Point {
  x: number;
  y: number;
}

export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface BerthSlot extends Point {
  /** Which flank of the mothership: -1 port, 1 starboard (in hull space) */
  side: -1 | 1;
}

export interface BaseLayout {
  width: number;
  height: number;
  /** Mothership centre before sway */
  center: Point;
  /** Mothership heading: 0 is nose-right, -PI/2 nose-up */
  angle: number;
  /** Unit scale of the mothership hull */
  size: number;
  /** Docked ships are drawn this much larger than in combat */
  shipScale: number;
  /** Radius of a berth pad and of a docked ship's hit area */
  berthRadius: number;
  /** Berth positions relative to `center` */
  berths: BerthSlot[];
  /** Nose-up layouts have no room beside the ships, so labels go underneath */
  labelsBelow: boolean;
}

/** Along-hull positions of the berths on each flank, best first (hull units, nose is +x) */
const BERTH_STATIONS = [0.08, -0.46, 0.62, -1.0, 1.12];

/** Screen space kept clear for the HTML panels, matching the base screen's CSS breakpoints. */
export function baseInsets(width: number, height: number): Insets {
  if (height <= 520 && width > height) return { top: 8, right: 250, bottom: 8, left: 8 };
  if (width < 1000) return { top: 76, right: 12, bottom: Math.min(270, Math.round(height * 0.34)), left: 12 };
  return { top: 84, right: 300, bottom: 100, left: 290 };
}

export function computeBaseLayout(width: number, height: number, insets: Insets = baseInsets(width, height)): BaseLayout {
  const availWidth = Math.max(100, width - insets.left - insets.right);
  const availHeight = Math.max(100, height - insets.top - insets.bottom);
  const landscape = availWidth >= availHeight * 1.1;
  const angle = landscape ? 0 : -Math.PI / 2;
  const along = landscape ? availWidth : availHeight;
  const across = landscape ? availHeight : availWidth;

  // The hull and its engine glow span ~3.2 units. Across the hull, berths and docked ships need ~1.56 units,
  // plus room for the labels: above and below the ships when nose-right, only beside the ships when nose-up
  const labelRoom = landscape ? 170 : 70;
  const size = Math.max(60, Math.min(320, along / 3.2, (across - labelRoom) / 1.56));
  const shipScale = Math.max(1.1, Math.min(2, size / 120));
  const berthRadius = 14 * shipScale * 1.3;
  const dockOffset = size * 0.5 + Math.max(30, size * 0.28);

  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const rotate = (x: number, y: number): Point => ({ x: x * cos - y * sin, y: x * sin + y * cos });

  // Centre the hull plus its engine glow in the free area
  const shift = rotate(size * 0.1, 0);
  const center = {
    x: insets.left + availWidth / 2 + shift.x,
    y: insets.top + availHeight / 2 + shift.y,
  };

  const berths = Array.from({ length: BERTH_COUNT }, (_, i): BerthSlot => {
    const side: -1 | 1 = i % 2 === 0 ? -1 : 1;
    const station = BERTH_STATIONS[Math.floor(i / 2) % BERTH_STATIONS.length];
    return { ...rotate(station * size, side * dockOffset), side };
  });

  return { width, height, center, angle, size, shipScale, berthRadius, berths, labelsBelow: !landscape };
}

export function berthPosition(layout: BaseLayout, berth: number, sway: Point = { x: 0, y: 0 }): Point {
  const slot = layout.berths[berth];
  return { x: layout.center.x + slot.x + sway.x, y: layout.center.y + slot.y + sway.y };
}

/** Direction pointing away from the hull at a berth. */
export function outwardAngle(layout: BaseLayout, berth: number): number {
  return layout.angle + (layout.berths[berth].side * Math.PI) / 2;
}

export type BaseTarget = { kind: 'ship'; id: number } | { kind: 'berth'; berth: number };

/** What sits under the pointer: a docked ship, or an empty berth. */
export function pickBaseTarget(
  layout: BaseLayout,
  ships: { id: number; berth: number; x: number; y: number }[],
  x: number,
  y: number,
  touch: boolean
): BaseTarget | null {
  const reach = layout.berthRadius * (touch ? 1.6 : 1);

  let best: { id: number; distance: number } | null = null;
  for (const ship of ships) {
    const distance = Math.hypot(ship.x - x, ship.y - y);
    if (distance <= reach && (!best || distance < best.distance)) best = { id: ship.id, distance };
  }
  if (best) return { kind: 'ship', id: best.id };

  const occupied = new Set(ships.map(s => s.berth));
  for (let berth = 0; berth < layout.berths.length; berth++) {
    if (occupied.has(berth)) continue;
    const p = berthPosition(layout, berth);
    if (Math.hypot(p.x - x, p.y - y) <= reach) return { kind: 'berth', berth };
  }
  return null;
}
